import React from 'react';
import { FileTextOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import type { NavigateFunction } from 'react-router-dom';
import { DropdownMenu, Icon, Tooltip } from '@hydrodigital/design-system';
import { LogoutIconSvg } from '@/assets/svg';
import { FUTURE_FEATURES } from '../constants';
import { BuildingIcon, ChevronDownIcon, UserAvatarMaskDataUrl } from '../Icons';
import TooltipLabel from './TooltipLabel';

interface UserMenuProps {
	name: string;
	role: string | undefined;
	isSmallScreen: boolean;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	navigate: NavigateFunction;
	onContactSupport: () => void;
	onLogout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
	name,
	role,
	isSmallScreen,
	open,
	onOpenChange,
	navigate,
	onContactSupport,
	onLogout,
}) => {
	const displayNameLabel = React.useMemo(() => {
		const s = String(name || 'User');
		return s.length > 15 ? `${s.slice(0, 15)}...` : s;
	}, [name]);

	const isAdminRole = role === 'Administrator' || role === 'SuperAdmin';
	const hasMenuItems = role === 'Operator' || isAdminRole;

	return (
		<Tooltip position="down" minWidth={20} slot={<TooltipLabel>User Menu</TooltipLabel>}>
			<div className="mr-2 relative flex items-center h-8">
				<DropdownMenu open={open} onOpenChange={onOpenChange}>
					<DropdownMenu.Trigger>
						<div
							className="flex items-center justify-between h-8"
							style={{
								borderRadius: 20,
								padding: isSmallScreen ? '0 6px' : '0 12px',
								backgroundColor: 'white',
								color: '#434B56',
								border: '1px solid #75798f',
								justifyContent: isSmallScreen ? 'center' : undefined,
							}}
						>
							<span
								style={{
									display: 'inline-flex',
									alignItems: 'center',
									marginRight: isSmallScreen ? 0 : 8,
									transform: 'none',
									transition: 'none',
								}}
							>
								<span
									style={{
										width: 16,
										height: 16,
										display: 'inline-block',
										backgroundColor: 'currentColor',
										WebkitMaskImage: UserAvatarMaskDataUrl,
										WebkitMaskRepeat: 'no-repeat',
										WebkitMaskSize: 'contain',
										maskImage: UserAvatarMaskDataUrl,
										maskRepeat: 'no-repeat',
										maskSize: 'contain',
									}}
								/>
							</span>
							{!isSmallScreen && <span style={{ fontSize: 12 }}>{displayNameLabel}</span>}
							{!isSmallScreen && <ChevronDownIcon />}
						</div>
					</DropdownMenu.Trigger>
					<DropdownMenu.Content width={240}>
						{FUTURE_FEATURES.myProfileMenuItem && isAdminRole && (
							<DropdownMenu.Item icon={<UserOutlined />} onSelect={() => navigate('/users')}>
								My Profile
							</DropdownMenu.Item>
						)}
						{isAdminRole && (
							<DropdownMenu.Item icon={<TeamOutlined />} onSelect={() => navigate('/users')}>
								User Manager
							</DropdownMenu.Item>
						)}
						{role === 'SuperAdmin' && (
							<DropdownMenu.Item icon={<BuildingIcon />} onSelect={() => navigate('/organizations')}>
								Organization Manager
							</DropdownMenu.Item>
						)}
						{hasMenuItems && (
							<DropdownMenu.Item icon={<FileTextOutlined />} onSelect={onContactSupport}>
								Contact Support
							</DropdownMenu.Item>
						)}
						{isAdminRole && <DropdownMenu.Separator />}
						{hasMenuItems && (
							<DropdownMenu.Item
								icon={<Icon children={<LogoutIconSvg />} />}
								destructive
								onSelect={onLogout}
							>
								Log Out
							</DropdownMenu.Item>
						)}
					</DropdownMenu.Content>
				</DropdownMenu>
			</div>
		</Tooltip>
	);
};

export default UserMenu;
